import { UIView } from "@atlas-int/data";
import { useMemo } from 'react';
import { OverTimeLayoutOption } from '../constants';
import { MappedSeriesData, RawSeriesData, SeriesDataPoint } from '../types';

interface UseSeriesDataMapperProps {
  seriesData: RawSeriesData[] | undefined;
  view: UIView;
  locationLevel: string;
  productLevel?: number;
  scaledTradeValueSelector: string;
  overTimeLayout: OverTimeLayoutOption;
  yScale: (value: number) => number;
}

export const useSeriesDataMapper = ({
  seriesData,
  view,
  locationLevel,
  productLevel,
  scaledTradeValueSelector,
  overTimeLayout,
  yScale,
}: UseSeriesDataMapperProps): MappedSeriesData[] => {
  return useMemo(() => {
    if (!seriesData) return [];

    return seriesData.map((series: RawSeriesData) => {
      let seriesKey = series.key;
      let useId: string | undefined = series.topLevelParent;
      let nameEn: string | undefined = series.nameEn;
      let topLevelParentName: string | undefined = series.topLevelParentName;

      let metadataProperties: Record<string, unknown>;
      if (view === UIView.Markets) {
        if (locationLevel === 'country') {
          metadataProperties = {
            countryId: seriesKey,
            nameEn,
            topLevelParent: series.topLevelParent,
          };
        } else {
          metadataProperties = {
            groupId: seriesKey,
            nameEn,
            groupName: nameEn,
            topLevelParent: series.topLevelParent,
          };
        }
      } else if (view === UIView.Products) {
        metadataProperties = {
          productId: seriesKey,
          nameEn,
          topLevelParent: series.topLevelParent,
          topLevelParentName,
          productLevel,
        };
      }

      const mappedSeries: SeriesDataPoint[] = series.seriesData
        .map((d) => {
          let year = d.data.year as number;
          if (seriesKey in d.data) {
            let tradeDataForSeries = d.data[seriesKey] as Record<string, Record<string, number>>;
            let { exportValue, importValue } =
              tradeDataForSeries[scaledTradeValueSelector];
            let shareForYear;
            if (overTimeLayout === OverTimeLayoutOption.Share) {
              /* When the stack offset is set to stackOffsetExpand, the share 
                of the product for the given year is just the difference between the 
                min and max coordinates for the ribbon in the year, since all ribbons 
                are expanded to a baseline of 0% and maximum of 100%. 

                Important: Note that since this value is being computed using the stack generator, 
                we don't compute a separate share for exports or imports -- rather, 
                the imputed share value here is based on which trade direction 
                is being used to generate the ribbons themselves.

                See: https://d3js.org/d3-shape/stack#stackOffsetExpand
                */
              shareForYear = d[1] - d[0];
            } else {
              shareForYear = undefined;
            }
            const kind = view === UIView.Markets ? "location" as const : "product" as const;
            return {
              year,
              tradeValueMin: d[0],
              tradeValueMax: d[1],
              pointMin: yScale(d[0]),
              pointMax: yScale(d[1]),
              properties: {
                exportValue,
                importValue,
                shareForYear,
                ...metadataProperties,
              },
              kind,
            };
          } else {
            return undefined;
          }
        })
        .filter((d) => d !== undefined) as SeriesDataPoint[];

      return {
        seriesKey,
        mappedSeries,
        useId,
      };
    });
  }, [
    seriesData,
    view,
    locationLevel,
    productLevel,
    scaledTradeValueSelector,
    overTimeLayout,
    yScale,
  ]);
};

